import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { DISABLED_INERT } from '@/components/ui/button';
import { printInIsolatedFrame } from '@/lib/print-frame';
import {
  type SessionExportFormat,
  fetchSessionExportHtml,
  isSessionInTrash,
  sessionExportFileName,
  sessionExportUrl,
} from '@/lib/session-api';
import { useMutation } from '@tanstack/react-query';
import {
  Braces,
  ChevronDown,
  ChevronUp,
  FileText,
  LoaderCircle,
  Printer,
  Text as TextIcon,
} from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

/**
 * « Exporter en » - the ONE menu of the parcours and the conversation (§12.5,
 * §12.6, maquette `docs/specs/maquettes/sessions/export/`, issue #173).
 *
 * Four entries in two groups: PDF and HTML for a reader outside the
 * administration, then Markdown and JSON for the team. All four are rendered by
 * the SERVER, so two people export the same content (§12.5).
 *
 * ## A disclosure, not `ActionMenu`
 *
 * `ActionMenu`'s items are buttons that close the menu as they fire. Three of
 * these entries are DOWNLOADS - links, which a reader may right-click, copy or
 * open in a tab - and the fourth has to keep the panel open while it prepares
 * (« le reste du menu restant utilisable »). A `role="menu"` holding links and a
 * button that does not close would promise a widget and hand over something
 * else, so this is a disclosure: a button with `aria-expanded`, and a panel of
 * two labelled groups that Tab walks like any other part of the page.
 *
 * ## « PDF » is the print of the HTML (H107)
 *
 * The entry reads `export.html`, loads it in a hidden, sandboxed frame and asks
 * that frame to print (`lib/print-frame.ts`, where the sandbox is argued). While
 * it reads, the entry says « Préparation du PDF... » under a ring and is
 * `aria-busy`, and a second press does nothing - it is NOT disabled, which
 * would throw the keyboard's focus to `<body>` mid-gesture. The three other
 * entries stay live. A read that fails is said inside the panel, which reopens
 * for it if it had been closed meanwhile.
 *
 * ## In the trash, a button that is really disabled
 *
 * §12.5: a session in the trash no longer exports. The trigger is `disabled`
 * under `DISABLED_INERT` and « Restaurez la session pour l’exporter. » is written
 * under it - never `aria-disabled` on a live button - and no panel exists, so
 * nothing can send a request the four routes would refuse anyway (H116).
 *
 * ## Above the layer bar, below the overlays
 *
 * On the conversation the panel opens over the sticky bar of the six layers,
 * which is `z-20` and comes LATER in the document: at an equal index the later
 * one paints on top. `z-25` is the one step between it and the `z-30` / `z-40`
 * that the full-screen overlays and the modals hold.
 */

/**
 * The shape of the two buttons at the head of the two screens: 36 px high,
 * 14 px, corners of 8 px. « Retour au parcours » takes it too - one component,
 * one style (§12.6) - and each caller adds its own colours.
 */
export const HEAD_ACTION_SHAPE =
  'inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-md border-2 bg-white font-semibold text-sm leading-none no-underline';

/** What « PDF » says when its document could not be read. */
export function pdfFailureSentence(error: unknown): string {
  return isSessionInTrash(error)
    ? 'Cette session est à la corbeille : restaurez-la pour l’exporter.'
    : 'Le document n’a pas pu être lu. Réessayez.';
}

type ExportFormat = 'pdf' | SessionExportFormat;

interface ExportEntry {
  readonly format: ExportFormat;
  readonly label: string;
  readonly help: string;
  readonly icon: ReactNode;
}

const ICON = 'mt-px size-[18px] shrink-0 text-gray-600';

/** The two groups of §12.6, in their order. The labels are the spec's. */
const GROUPS: readonly { readonly label: string; readonly entries: readonly ExportEntry[] }[] = [
  {
    label: 'Pour un lecteur extérieur',
    entries: [
      {
        format: 'pdf',
        label: 'PDF',
        help: 'à imprimer ou à enregistrer, sans l’email',
        icon: <Printer aria-hidden="true" className={ICON} />,
      },
      {
        format: 'html',
        label: 'HTML',
        help: 'un fichier à envoyer, sans l’email',
        icon: <FileText aria-hidden="true" className={ICON} />,
      },
    ],
  },
  {
    label: 'Pour l’équipe',
    entries: [
      {
        format: 'md',
        label: 'Markdown',
        help: 'à coller dans un fil',
        icon: <TextIcon aria-hidden="true" className={ICON} />,
      },
      {
        format: 'json',
        label: 'JSON',
        help: 'les tours, pour déboguer',
        icon: <Braces aria-hidden="true" className={ICON} />,
      },
    ],
  },
];

const ENTRY =
  'flex w-full items-start gap-2.5 px-3 py-2 text-left no-underline hover:bg-bleu-nuit-tint focus-visible:bg-bleu-nuit-tint focus-visible:outline-none';

export interface ExportMenuProps {
  readonly sessionId: string;
  /** `deletedAt !== null`: the menu is a disabled button and says why. */
  readonly trashed: boolean;
}

export function ExportMenu({ sessionId, trashed }: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const reasonId = useId();
  const groupId = useId();

  const pdf = useMutation({
    mutationFn: async () => printInIsolatedFrame(await fetchSessionExportHtml(sessionId)),
    // The print box is open: the menu has done its job.
    onSuccess: () => setOpen(false),
    // Said IN the panel (H107), so a panel closed meanwhile comes back for it.
    onError: () => setOpen(true),
  });

  // Escape and the outside press, the two ways out of a disclosure. Listeners on
  // the document and not data: nothing here loads anything.
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    }
    function onDown(event: MouseEvent) {
      const target = event.target;
      if (target instanceof Node && container.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  if (trashed) {
    return (
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <button
          type="button"
          disabled
          aria-describedby={reasonId}
          data-testid={SESSIONS_TEST_IDS.exportTrigger}
          className={`${HEAD_ACTION_SHAPE} border-bleu-nuit pr-3 pl-4 text-bleu-nuit disabled:cursor-not-allowed ${DISABLED_INERT}`}
        >
          Exporter en
          <ChevronDown aria-hidden="true" className="size-4 shrink-0" />
        </button>
        <p
          id={reasonId}
          data-testid={SESSIONS_TEST_IDS.exportTrashReason}
          className="text-gray-600 text-xs"
        >
          Restaurez la session pour l’exporter.
        </p>
      </div>
    );
  }

  const Chevron = open ? ChevronUp : ChevronDown;

  function entry(item: ExportEntry): ReactNode {
    const text = (help: ReactNode) => (
      <span className="flex min-w-0 flex-col">
        <span className="font-semibold text-gray-900 text-sm">{item.label}</span>
        <span className="text-gray-600 text-xs">{help}</span>
      </span>
    );

    if (item.format === 'pdf') {
      const busy = pdf.isPending;
      return (
        <div key="pdf">
          <button
            type="button"
            data-testid={SESSIONS_TEST_IDS.exportEntry}
            data-format="pdf"
            aria-busy={busy}
            onClick={() => {
              if (!busy) pdf.mutate();
            }}
            className={`${ENTRY} ${busy ? 'cursor-progress' : 'cursor-pointer'}`}
          >
            {item.icon}
            {text(
              busy ? (
                <span className="inline-flex items-center gap-1.5">
                  <LoaderCircle
                    aria-hidden="true"
                    className="size-[13px] shrink-0 motion-safe:animate-spin"
                  />
                  Préparation du PDF...
                </span>
              ) : (
                item.help
              ),
            )}
          </button>
          {pdf.isError ? (
            <p
              role="alert"
              data-testid={SESSIONS_TEST_IDS.exportError}
              className="pr-3 pb-2 pl-10 text-rouge-deep text-xs"
            >
              {pdfFailureSentence(pdf.error)}
            </p>
          ) : null}
        </div>
      );
    }

    return (
      <a
        key={item.format}
        href={sessionExportUrl(sessionId, item.format)}
        download={sessionExportFileName(sessionId, item.format)}
        data-testid={SESSIONS_TEST_IDS.exportEntry}
        data-format={item.format}
        onClick={() => setOpen(false)}
        className={ENTRY}
      >
        {item.icon}
        {text(item.help)}
      </a>
    );
  }

  return (
    <div ref={container} className="relative shrink-0">
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-testid={SESSIONS_TEST_IDS.exportTrigger}
        onClick={() => {
          // A failure is said for the attempt it belongs to, not for the next
          // opening of the menu.
          if (!open && pdf.isError) pdf.reset();
          setOpen(!open);
        }}
        className={`${HEAD_ACTION_SHAPE} cursor-pointer border-bleu-nuit pr-3 pl-4 text-bleu-nuit hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
      >
        Exporter en
        <Chevron aria-hidden="true" className="size-4 shrink-0" />
      </button>
      {open ? (
        <div
          id={panelId}
          data-testid={SESSIONS_TEST_IDS.exportPanel}
          className="absolute top-full right-0 z-25 mt-1 w-72 rounded-md border border-gray-200 bg-white py-1.5 shadow-md"
        >
          {GROUPS.map((group, index) => (
            <div key={group.label}>
              {index === 0 ? null : <div aria-hidden="true" className="my-1.5 h-px bg-gray-100" />}
              {/* biome-ignore lint/a11y/useSemanticElements: a group of download links, not of form controls - a <fieldset> would announce a form. */}
              <div role="group" aria-labelledby={`${groupId}-${index}`} className="flex flex-col">
                <p
                  id={`${groupId}-${index}`}
                  className="px-3 pt-1.5 pb-1 font-semibold text-[11px] text-gray-600 uppercase leading-4 tracking-wide"
                >
                  {group.label}
                </p>
                {group.entries.map(entry)}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
